import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/auth-provider'
import { SignIn } from '@/auth/sign-in'
import { Verify } from '@/auth/verify'
import { NoAccess } from '@/auth/no-access'
import { AppShell } from '@/components/app-shell'
import { featureOn } from '@/lib/features'
import { ErrorNote } from '@/components/page'
import { DashboardPage } from '@/pages/dashboard'
import { TodayPage } from '@/pages/today'
import { ActivitiesPage } from '@/pages/activities'
import { ActivityDetailPage } from '@/pages/activity-detail'
import { SchedulePage } from '@/pages/schedule'
import { CoachingPage } from '@/pages/coaching'
import { PresentPage } from '@/pages/present'
import { EvaluationsPage } from '@/pages/evaluations'
import { EvaluationDetailPage } from '@/pages/evaluation-detail'
import { TechStackDetailPage } from '@/pages/tech-stack-detail'
import { ReportsPage } from '@/pages/reports'
import { ReportDetailPage } from '@/pages/report-detail'
import { WorkshopsPage } from '@/pages/workshops'
import { DiscussionPage } from '@/pages/discussion'
import { BoardsPage } from '@/pages/boards'
import { AccessPage } from '@/pages/access'
import { BoardCanvasPage } from '@/pages/board-canvas'
import { AdminPage } from '@/pages/admin'
import { MissionControlPage } from '@/pages/mission-control'
import { CommonGoalsPage } from '@/pages/common-goals'
import { StatusPage } from '@/pages/status'
import { CertificationsPage } from '@/pages/certifications'
import { GamesPage } from '@/pages/games'
import { SheepRacePage } from '@/pages/sheep-race'
import { MissileDefencePage } from '@/pages/missile-defence'
import { RacingPage } from '@/pages/racing'
import { MissileDefenceTeamPage } from '@/pages/missile-defence-team'
import { CertificationStatusPage } from '@/pages/certification-status'
import { SelfAssessmentPage } from '@/pages/self-assessment'
import { SelfAssessmentDetailPage } from '@/pages/self-assessment-detail'
import { SelfAssessmentTeamPage } from '@/pages/self-assessment-team'
import { CertificationWallPage } from '@/pages/certification-wall'
import { BadgeVerifyPage } from '@/pages/badge-verify'

export function App() {
  const { session, loading, memberships, error, isAdmin, isLead, isCoach, currentTeam } = useAuth()
  const location = useLocation()

  // /verify must run before any auth gate: it's what turns a clicked magic link
  // INTO a session, so it has to be reachable while signed out.
  //
  // /status the same, for a different reason: it is the page you open when the
  // app will not let you in, so it cannot be behind the thing that isn't
  // letting you in. It renders before `loading` too — the auth provider is
  // itself talking to a service this page exists to report on, and a status
  // page that spins because auth is down is no status page at all.
  if (location.pathname === '/verify') {
    return (
      <Routes>
        <Route path="/verify" element={<Verify />} />
      </Routes>
    )
  }
  if (location.pathname === '/status') {
    return (
      <Routes>
        <Route path="/status" element={<StatusPage />} />
      </Routes>
    )
  }
  // /certified is public too: a certification is worth something partly because
  // someone outside the portal can see it. It reads one function that returns
  // only opted-in, approved holders — see migrations/0040_certification_wall.sql.
  if (location.pathname === '/certified') {
    return (
      <Routes>
        <Route path="/certified" element={<CertificationWallPage />} />
      </Routes>
    )
  }
  // /badges/:guid is where a badge's QR code lands, so it must open for whoever
  // scanned it. It reads one function that answers only for a complete record —
  // see migrations/0066_certification_badges.sql.
  // Exactly one segment, like the exact matches above: a looser prefix would
  // pull any other /badges/… path out of the app and render nothing.
  if (/^\/badges\/[^/]+\/?$/.test(location.pathname)) {
    return (
      <Routes>
        <Route path="/badges/:guid" element={<BadgeVerifyPage />} />
      </Routes>
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!session) return <SignIn />

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <ErrorNote error={error} />
      </div>
    )
  }

  // Authenticated but unaffiliated. DPL admins are let through even with no
  // teams so they can create the first one.
  if (memberships.length === 0 && !isAdmin) return <NoAccess />

  return (
    <Routes>
      {/* Outside AppShell on purpose: a deck being presented to a room should
          have the whole viewport, not two thirds of it.

          Gated with Topics rather than on its own: presenting is reached FROM a
          topic, so a team without Topics has no way in — and leaving the route
          registered would keep the live-session invite (components/
          presentation-invite.tsx) able to navigate people into a section their
          team switched off. */}
      {featureOn(currentTeam, 'topics_enabled') && (
        <Route path="/activities/:id/present" element={<PresentPage />} />
      )}
      <Route element={<AppShell />}>
        {/* A DPL admin with no team selected — usually one who has just signed
            in before any team exists — lands in Mission Control, since the
            dashboard has nothing to show them. */}
        <Route
          index
          element={<Navigate to={currentTeam || !isAdmin ? '/dashboard' : '/mission-control'} replace />}
        />
        <Route path="/dashboard" element={<DashboardPage />} />
        {/* Today exists only on a day with a meeting or something on the plan,
            and the condition is asked INSIDE the page (lib/today.ts) rather
            than here. The route table is synchronous and the answer is a
            query, so gating the route would mean bouncing a direct link to
            /today off to the dashboard for as long as that query took — and
            then a second time, correctly, when it came back empty. The page
            redirects instead, once, when it knows. */}
        <Route path="/today" element={<TodayPage />} />
        {/* Per-team section switches (migrations/0044_team_features.sql). These
            hide a section; they do not withhold data — the tables keep the
            policies they always had. Dropping the route as well as the nav entry
            matters because a stale bookmark or a link in an old message would
            otherwise open a page the team has switched off; `*` below sends
            those to the dashboard. */}
        {featureOn(currentTeam, 'topics_enabled') && (
          <Route path="/activities" element={<ActivitiesPage />} />
        )}
        {featureOn(currentTeam, 'topics_enabled') && (
          <Route path="/activities/:id" element={<ActivityDetailPage />} />
        )}
        {featureOn(currentTeam, 'schedule_enabled') && (
          <Route path="/schedule" element={<SchedulePage />} />
        )}
        <Route path="/evaluations" element={<EvaluationsPage />} />
        <Route path="/evaluations/:id" element={<EvaluationDetailPage />} />
        {/* A Tech Stack review is listed with the evaluations but is not one:
            no scores, no scale, its own tables. Hence its own route. */}
        <Route path="/tech-stack/:id" element={<TechStackDetailPage />} />
        {/* Reports are for the team lead and DPL staff — a plain member has no
            route to them at all. RLS is the control (migrations/0018_reports.sql):
            it returns nothing to a member and refuses every write from anyone but
            a coach. This guard just keeps a stray link from rendering an empty
            page. */}
        {isLead && featureOn(currentTeam, 'reports_enabled') && (
          <Route path="/reports" element={<ReportsPage />} />
        )}
        {isLead && featureOn(currentTeam, 'reports_enabled') && (
          <Route path="/reports/:id" element={<ReportDetailPage />} />
        )}
        {/* The individual self-assessment (0067). Every member takes their own;
            the team view is DPL-coach-only, and RLS is the control there — a
            lead gets no rows. /team is registered before /:id so it is not read
            as an id. */}
        <Route path="/self-assessment" element={<SelfAssessmentPage />} />
        {isCoach && <Route path="/self-assessment/team" element={<SelfAssessmentTeamPage />} />}
        <Route path="/self-assessment/:id" element={<SelfAssessmentDetailPage />} />
        {/* 1:1 coaching notes are DPL-coach-only — narrower than every other
            surface here, including Reports, which the lead reads. RLS is the
            control (migrations/0031_coaching.sql): it returns nothing to a lead
            or to the person the notes are about. This guard only keeps a stray
            link from rendering an empty page. */}
        {isCoach && featureOn(currentTeam, 'coaching_enabled') && (
          <Route path="/coaching" element={<CoachingPage />} />
        )}
        {/* The tab exists only when a coach has turned it on for this team
            (migrations/0037). RLS returns nothing either way, so this is the
            courtesy and the policy is the control. */}
        {currentTeam?.certifications_enabled && (
          <Route path="/certifications" element={<CertificationsPage />} />
        )}
        {currentTeam?.certifications_enabled && isCoach && (
          <Route path="/certifications/status" element={<CertificationStatusPage />} />
        )}
        {featureOn(currentTeam, 'workshops_enabled') && (
          <Route path="/workshops" element={<WorkshopsPage />} />
        )}
        {featureOn(currentTeam, 'discussion_enabled') && (
          <Route path="/discussion" element={<DiscussionPage />} />
        )}
        <Route path="/boards" element={<BoardsPage />} />
        <Route path="/boards/:id" element={<BoardCanvasPage />} />

        {/* Games are off until a team turns them on (0046). Routed behind the
            same switch as the nav entry, so a stale link cannot reach a page
            the sidebar does not offer. */}
        {featureOn(currentTeam, 'games_enabled') && (
          <Route path="/games" element={<GamesPage />} />
        )}
        {featureOn(currentTeam, 'games_enabled') && (
          <Route path="/games/sheep-race/:sessionId" element={<SheepRacePage />} />
        )}
        {/* No :sessionId — Missile Defence is played alone and stores nothing
            (0060), so there is no session to name. */}
        {featureOn(currentTeam, 'games_enabled') && (
          <Route path="/games/missile-defence" element={<MissileDefencePage />} />
        )}
        {/* Each player's own sky; the lobby and the scoreboard are 0061. */}
        {featureOn(currentTeam, 'games_enabled') && (
          <Route path="/games/missile-defence-team/:lobbyId" element={<MissileDefenceTeamPage />} />
        )}
        {/* Racing is played alone too (0062); its only state is the times. */}
        {featureOn(currentTeam, 'games_enabled') && (
          <Route path="/games/racing" element={<RacingPage />} />
        )}

        {isAdmin && <Route path="/access" element={<AccessPage />} />}
        {isAdmin && <Route path="/mission-control" element={<MissionControlPage />} />}
        {isAdmin && <Route path="/mission-control/goals" element={<CommonGoalsPage />} />}
        {isAdmin && <Route path="/admin" element={<AdminPage />} />}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  )
}
